import { useTranslation } from '@spcc/core'
import {
  CheckCircle2,
  Archive,
  Banknote,
  FileText,
  StatsWidget,
  StatsWidgetGrid,
  cn,
} from '@spcc/uikit'
import type { SalesOrderStatus } from '../../../../domain/types'
import { formatCurrency } from '../utils'

interface SalesOrdersStatsProps {
  stats: {
    active: number
    completed: number
    draft: number
    totalValue: { amount: number; currency: string }
  }
  filter: { status?: SalesOrderStatus[] }
  onStatusFilter: (status: SalesOrderStatus) => void
}

function isStatusSelected(statuses: SalesOrderStatus[] | undefined, status: SalesOrderStatus): boolean {
  return Array.isArray(statuses) && statuses.includes(status)
}

function resolveStatusRingClass(
  statuses: SalesOrderStatus[] | undefined,
  status: SalesOrderStatus,
  ringClass: string
): string {
  return isStatusSelected(statuses, status) ? ringClass : ''
}

function createStatusClickHandler(onStatusFilter: (status: SalesOrderStatus) => void, status: SalesOrderStatus) {
  return () => onStatusFilter(status)
}

export function SalesOrdersStats({ stats, filter, onStatusFilter }: SalesOrdersStatsProps) {
  const { t } = useTranslation()

  return (
    <StatsWidgetGrid columns={4}>
      <StatsWidget
        label={t('salesOrders.draft')}
        value={stats.draft}
        icon={FileText}
        color="slate"
        onClick={createStatusClickHandler(onStatusFilter, 'draft')}
        className={cn(resolveStatusRingClass(filter.status, 'draft', 'ring-2 ring-slate-500'))}
      />
      <StatsWidget
        label={t('salesOrders.active')}
        value={stats.active}
        icon={CheckCircle2}
        color="emerald"
        onClick={createStatusClickHandler(onStatusFilter, 'active')}
        className={cn(resolveStatusRingClass(filter.status, 'active', 'ring-2 ring-emerald-500'))}
      />
      <StatsWidget
        label={t('salesOrders.completed')}
        value={stats.completed}
        icon={Archive}
        color="indigo"
        onClick={createStatusClickHandler(onStatusFilter, 'completed')}
        className={cn(resolveStatusRingClass(filter.status, 'completed', 'ring-2 ring-indigo-500'))}
      />
      <StatsWidget
        label={t('salesOrders.totalValue')}
        value={`SAR ${formatCurrency(stats.totalValue.amount)}`}
        icon={Banknote}
        color="blue"
      />
    </StatsWidgetGrid>
  )
}
